var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AugLoopTextDecoder, AugLoopTextEncoder } from './augloop-text-encoding';
/**
 * To get quick view how JSOND format of the message looks like go to modules\jsond-stream\src\jsond-stream.ts
 */
export class JSOND {
    static extractFragments(data) {
        if (data[0] !== JSOND.IDENTIFIERBYTE) {
            throw new Error(`Invalid Binary: Incorrect Identifier`);
        }
        let offset = 1;
        const fragments = [];
        const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
        while (offset < data.byteLength) {
            if (offset + 4 > data.byteLength) {
                throw new Error(`Invalid Binary: Error reading fragment length`);
            }
            const fragmentLength = view.getUint32(offset);
            if (offset + fragmentLength + 4 > data.byteLength) {
                throw new Error(`Invalid Binary: Fragment out of range`);
            }
            if (typeof Buffer !== 'undefined' && Buffer.from) {
                fragments.push(Buffer.from(data.buffer, data.byteOffset + offset + 4, fragmentLength));
            }
            else {
                fragments.push(new Uint8Array(data.buffer, data.byteOffset + offset + 4, fragmentLength));
            }
            offset += 4 + fragmentLength;
        }
        if (fragments.length < 1) {
            throw new Error(`Invalid Binary: No fragments found`);
        }
        return fragments;
    }
    static deserialize(data, binaryFieldConverter = (data) => data) {
        const fragments = JSOND.extractFragments(data);
        const jsonText = JSOND.textDecoder.decode(fragments[0]);
        const reviver = (_key, value) => {
            if ('string' === typeof value) {
                if (value.substring(0, JSOND.BINARYKEYWORD.length) === JSOND.BINARYKEYWORD) {
                    const index = parseInt(value.substring(JSOND.BINARYKEYWORD.length), 10);
                    if (typeof index !== 'number' || index >= fragments.length - 1) {
                        throw new Error(`Invalid Binary: Binary index out of range`);
                    }
                    return binaryFieldConverter(fragments[index + 1]);
                }
                else if (value.substring(0, JSOND.ESCAPEKEYWORD.length) === JSOND.ESCAPEKEYWORD) {
                    return value.substring(JSOND.ESCAPEKEYWORD.length);
                }
            }
            return value;
        };
        return JSON.parse(jsonText, reviver);
    }
    static deserializeAsync(data, binaryFieldConverter = (data) => __awaiter(this, void 0, void 0, function* () { return data; })) {
        return __awaiter(this, void 0, void 0, function* () {
            const fragments = JSOND.extractFragments(data);
            const jsonText = JSOND.textDecoder.decode(fragments[0]);
            const pendingPromises = [];
            const createPlaceHolder = (promise) => {
                // The placeholder object will be be updated with the resolved value.
                const placeholder = { [PLACEHOLDER]: true, value: undefined };
                promise.then((resolved) => {
                    placeholder.value = resolved;
                });
                return placeholder;
            };
            const reviver = (_key, value) => {
                if ('string' === typeof value) {
                    if (value.startsWith(JSOND.BINARYKEYWORD)) {
                        const index = parseInt(value.substring(JSOND.BINARYKEYWORD.length), 10);
                        if (typeof index !== 'number' || index >= fragments.length - 1) {
                            throw new Error('Invalid Binary: Binary index out of range');
                        }
                        // Instead of awaiting immediate conversion, save the promise externally.
                        const promise = binaryFieldConverter(fragments[index + 1]);
                        pendingPromises.push(promise);
                        return createPlaceHolder(promise);
                    }
                    else if (value.startsWith(JSOND.ESCAPEKEYWORD)) {
                        return value.substring(JSOND.ESCAPEKEYWORD.length);
                    }
                }
                return value;
            };
            const PLACEHOLDER = Symbol('placeholder');
            const replacePlaceholders = (node) => {
                // If node is an array, process each element.
                if (Array.isArray(node)) {
                    // Replace each element with its recursively processed value.
                    for (let i = 0; i < node.length; i++) {
                        node[i] = replacePlaceholders(node[i]);
                    }
                    return node;
                }
                else if (node !== null && typeof node === 'object') {
                    // Check if the object is marked as a placeholder.
                    if (node[PLACEHOLDER]) {
                        // If it is a placeholder, return its value.
                        return node.value;
                    }
                    // Otherwise, process each property in the object.
                    for (const key of Object.keys(node)) {
                        node[key] = replacePlaceholders(node[key]);
                    }
                    return node;
                }
                else {
                    return node;
                }
            };
            let parsedObject = JSON.parse(jsonText, reviver);
            yield Promise.all(pendingPromises);
            parsedObject = yield replacePlaceholders(parsedObject);
            return parsedObject;
        });
    }
    static serializeInternal(obj) {
        const fragments = [undefined];
        const replacer = (_key, value) => {
            if (ArrayBuffer.isView(value)) {
                fragments.push(value);
                return `${JSOND.BINARYKEYWORD}${fragments.length - 2}`;
            }
            else if (value && value.type === 'Buffer' && Array.isArray(value.data)) {
                fragments.push(new Uint8Array(value.data));
                return `${JSOND.BINARYKEYWORD}${fragments.length - 2}`;
            }
            else if ('string' === typeof value && (value.substring(0, JSOND.ESCAPEKEYWORD.length) === JSOND.ESCAPEKEYWORD)) {
                return JSOND.ESCAPEKEYWORD + value;
            }
            return value;
        };
        const json = JSON.stringify(obj, replacer);
        fragments[0] = JSOND.textEncoder.encode(json);
        const aggregateLength = fragments.reduce((accumulator, buffer) => accumulator + 4 + buffer.byteLength, 0);
        const retVal = new Uint8Array(aggregateLength + 1);
        retVal[0] = JSOND.IDENTIFIERBYTE;
        let offset = 1;
        const view = new DataView(retVal.buffer, retVal.byteOffset, retVal.byteLength);
        for (const buffer of fragments) {
            view.setUint32(offset, buffer.byteLength);
            retVal.set(buffer, offset + 4);
            offset += 4 + buffer.byteLength;
        }
        return retVal;
    }
    static serialize(obj) {
        return JSOND.serializeInternal(obj);
    }
    static serializeAsync(obj) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => {
                try {
                    resolve(JSOND.serializeInternal(obj));
                }
                catch (error) {
                    reject(error);
                }
            });
        });
    }
}
JSOND.IDENTIFIERBYTE = 0x03;
JSOND.BINARYKEYWORD = ':b';
JSOND.ESCAPEKEYWORD = ':';
JSOND.textDecoder = new AugLoopTextDecoder();
JSOND.textEncoder = new AugLoopTextEncoder();
//# sourceMappingURL=jsond.js.map